import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ListResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T[];
  total: number;
}

export interface AuditEvent {
  id: string;
  module: string;
  event_type: string;
  actor_user_id: string;
  tenant_id: string | null;
  resource_type: string | null;
  resource_id: string | null;
  changed_fields: string[];
  previous_values: Record<string, unknown>;
  new_values: Record<string, unknown>;
  outcome: string | null;
  metadata: Record<string, unknown>;
  correlation_id: string | null;
  occurred_at: string;
}

export interface ActivityEvent {
  id: string | null;
  tenant_id: string | null;
  correlation_id: string | null;
  resource_type: string;
  resource_id: string;
  activity_type: string;
  actor_user_id: string;
  actor_name: string | null;
  detail: Record<string, unknown> | null;
  occurred_at: string;
}

export type LogLevel = 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';

export interface AppLogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  logger: string;
  module: string | null;
  func: string | null;
  line: number | null;
  message: string;
  exception: string | null;
  correlation_id: string | null;
  tenant_id: string | null;
  user_id: string | null;
}

export type TraceSource = 'audit' | 'activity' | 'app';

export interface TraceEvent {
  source: TraceSource;
  at: string;
  summary: string;
  level?: LogLevel;
  tenant_id: string | null;
  data: Record<string, unknown>;
}

export interface TraceResponse {
  success: boolean;
  message_code: number;
  message: string;
  data: {
    correlation_id: string;
    events: TraceEvent[];
    counts: Record<TraceSource, number>;
    truncated: boolean;
  };
}

interface Paging {
  tenant_id?: string;
  correlation_id?: string;
  from_date?: string;
  to_date?: string;
  limit?: number;
  skip?: number;
}

export interface AuditLogParams extends Paging {
  module?: string;
  event_type?: string;
  actor_user_id?: string;
  resource_id?: string;
}

export interface ActivityLogParams extends Paging {
  resource_type?: string;
  resource_id?: string;
  actor_user_id?: string;
  activity_type?: string;
}

export interface AppLogParams extends Paging {
  level?: LogLevel[];
  logger?: string;
  user_id?: string;
  search?: string;
}

/** Drops empty values so blank filters are not sent as `?x=`. */
function clean<T extends object>(params: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)),
  );
}

export const platformLogsApi = createApi({
  reducerPath: 'platformLogsApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    listAuditEvents: builder.query<ListResponse<AuditEvent>, AuditLogParams>({
      query: (params) => ({ url: '/api/v1/audit', params: clean(params) }),
    }),
    listActivityEvents: builder.query<ListResponse<ActivityEvent>, ActivityLogParams>({
      query: (params) => ({ url: '/api/v1/activity-log', params: clean(params) }),
    }),
    listAppLogs: builder.query<ListResponse<AppLogEntry>, AppLogParams>({
      // `level` repeats (?level=ERROR&level=WARNING) — build the query string by hand.
      query: ({ level, ...rest }) => {
        const qs = new URLSearchParams();
        Object.entries(clean(rest)).forEach(([k, v]) => qs.set(k, String(v)));
        (level ?? []).forEach((lv) => qs.append('level', lv));
        return `/api/v1/audit/app-logs?${qs.toString()}`;
      },
    }),
    getTrace: builder.query<TraceResponse, string>({
      query: (correlationId) => `/api/v1/audit/trace/${encodeURIComponent(correlationId)}`,
    }),
  }),
});

export const {
  useListAuditEventsQuery,
  useListActivityEventsQuery,
  useListAppLogsQuery,
  useGetTraceQuery,
} = platformLogsApi;
